import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import NetworkManager from "../../app/managers/networkManager"
import { config } from "../../app/utils/config"
import { showErrorToast, showSuccessToast } from '../../app/utils/helpers'
import { Localize } from '../../app/lang/langs'
import { setIsLoading } from "../../containers/common/commonSlice"
import { store } from "../../app/store/store"

const initialState = {
    managersProfiles: [],
    // The "add a manager to a team" picker, kept apart from `managersProfiles` on purpose —
    // see the getManagersProfiles reducer below.
    assignableProfiles: [],
    managersPermissions: {},
    managersList: [],
    teamsManagersList: [],
}

export const permissionsSlice = createSlice({
    name: 'permissions',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getManagersProfiles.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                // Two different lists come back from this one endpoint and they must not share
                // a slot. Called with `idTeam` it is the add-a-manager picker, which lists every
                // active profile INCLUDING ones hidden from the teams table; called without, it
                // is the curated list the teams-table columns are built from. Writing both to
                // `managersProfiles` made opening the modal graft a hidden profile's column onto
                // the table until the next refresh.
                if (action.meta?.arg?.idTeam) {
                    state.assignableProfiles = action.payload.responseData.data
                } else {
                    state.managersProfiles = action.payload.responseData.data
                }
            }
        })
        builder.addCase(getManagersPermissions.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                let profileId = action.meta?.arg?.profileId
                let profileName = action.meta?.arg?.profileName
                let perms = action.payload.responseData.managers_permissions
                if (profileName && perms) {
                    state.managersPermissions = { ...state.managersPermissions, [profileName]: perms[profileName] }
                } else {
                    state.managersPermissions = perms
                }
            }
        })
        builder.addCase(updateManagersPermissions.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                showSuccessToast("Autorisations mises à jour avec succès")
            } else {
                showErrorToast("Quelque chose s'est mal passé !")
            }
        })
        builder.addCase(addManagerProfile.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                // Localized rather than echoing the server's message: that string is English
                // ("Profile added successfully") and surfaced untranslated in the French UI.
                showSuccessToast(Localize('PROFILE_ADDED'))
            } else if (action.payload.responseData) {
                // Refusals are still shown verbatim — they name the teams or the profile that
                // caused them, which a generic translated string would throw away. They are
                // English too; localising them needs message codes from the API, not a
                // client-side lookup.
                showErrorToast(action.payload.responseData.message || Localize('PROFILE_SAVE_FAILED'))
            }
        })
        builder.addCase(editManagerProfile.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                // Localized rather than echoing the server's message: that string is English
                // ("Profile added successfully") and surfaced untranslated in the French UI.
                showSuccessToast(Localize('PROFILE_UPDATED'))
            } else if (action.payload.responseData) {
                // Refusals are still shown verbatim — they name the teams or the profile that
                // caused them, which a generic translated string would throw away. They are
                // English too; localising them needs message codes from the API, not a
                // client-side lookup.
                showErrorToast(action.payload.responseData.message || Localize('PROFILE_SAVE_FAILED'))
            }
        })
        builder.addCase(getManagersList.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                state.managersList = action.payload.responseData.data
            }
        })
        builder.addCase(getTeamsManagersList.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                state.teamsManagersList = action.payload.responseData.data
            }
        })
    },
})

export const getManagersProfiles = createAsyncThunk(
    'permissions/getManagersProfiles',
    async (params) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.getProfiles, params)
            return response
        } catch (error) {
            return error
        }
    }
)

export const addManagerProfile = createAsyncThunk(
    'permissions/addManagerProfile',
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.addProfile, body)
            return response
        } catch (error) {
            return error
        }
    }
)

export const editManagerProfile = createAsyncThunk(
    'permissions/editManagerProfile',
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.editProfile, body)
            return response
        } catch (error) {
            return error
        }
    }
)

export const getManagersPermissions = createAsyncThunk(
    'permissions/getManagersPermissions',
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.getPermissions, body)
            return response
        } catch (error) {
            return error
        }
    }
)

export const updateManagersPermissions = createAsyncThunk(
    'permissions/updateManagersPermissions',
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.updatePermissions, body)
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            return error
        }
    }
)

export const getTeamsManagersList = createAsyncThunk(
    'permissions/getTeamsManagersList',
    async (params) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.getTeamsManagersList, params)
            return response
        } catch (error) {
            return error
        }
    }
)

export const getManagersList = createAsyncThunk(
    'permissions/getManagersList',
    async (params) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(config.apiPaths.managersProfiles.getManagersList, params)
            return response
        } catch (error) {
            return error
        }
    }
)

export default permissionsSlice.reducer
